import { CommonModule } from '@angular/common';
import { registerLocaleFor } from 'app/shared/util/register-locale';
import { APP_INITIALIZER, inject, Injector, LOCALE_ID, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ToastrModule } from 'ngx-toastr';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AppService } from './app.service';
import { Cache } from './core/cache.servce';
import { DataLockService } from './core/data-lock/data-lock.service';
import { ServiceLocator } from './core/locator.servce';
import { MasterModule } from './master/master.module';
import { NavStateService } from './master/nav-state.service';
import { OrderModule } from './order/order.module';
import { GridService } from './shared/grid/grid.service';
import { LoaderService } from './shared/loader/loader.service';
import { TagAdministrationModule } from './tag/tag.module';
import { ChartReloadService } from './theme/core/chart-reload.service';
import { UserSettingsService } from './user/core/user-settings.service';
import { UserService } from './user/core/user.service';

import { UTC_DATE_FNS_PROVIDERS } from './shared/util/utc-date-fns.adapter';

import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { NgxsModule } from '@ngxs/store';
import { provideNgxMask } from 'ngx-mask';
import { OAuthModule, OAuthService } from 'angular-oauth2-oidc';
import { CacheFactory } from 'cachefactory';
import { environment } from '../environments/environment';
import { AuthService } from './auth/services/auth.service';
import { ConfigState } from './core/config/config.state';
import { DataLockDialogService } from './core/data-lock/data-lock-dialog.service';
import { PermissionsState } from './permissions/permissions.state';
import { ChartService } from './shared/chart/chart.service';
import { DefaultThemeResolve } from './theme/core/default-theme.resolve';
import { UserState } from './user/state/user.state';

const toastrOptions = {
	positionClass: 'toast-bottom-right'
};

function initializeAuthFactory() {
	const authService = inject(AuthService);
	const oauthService = inject(OAuthService);

	return async () => {
		void oauthService;
		try {
			await authService.initializeSso();
		} catch (error) {
			console.error('Error during SSO initialization:', error);
		}
	};
}

@NgModule({ declarations: [AppComponent],
    exports: [],
    bootstrap: [AppComponent], imports: [BrowserModule,
        CommonModule,
        BrowserAnimationsModule,
        AppRoutingModule,
        MasterModule,
        ToastrModule.forRoot(toastrOptions),
        OAuthModule.forRoot(),
        OrderModule,
        TagAdministrationModule,
        NgxsModule.forRoot([PermissionsState, UserState, ConfigState], {
            developmentMode: !environment.production
        })], providers: [
        provideNgxMask(),
        ...UTC_DATE_FNS_PROVIDERS,
        AppService,
        GridService,
        UserService,
        UserSettingsService,
        ChartReloadService,
        LoaderService,
        DataLockService,
        DataLockDialogService,
        NavStateService,
        DefaultThemeResolve,
        ChartService,
        ServiceLocator,
        {
            provide: LOCALE_ID,
            useFactory: () => (localStorage.getItem('__localeId') ? localStorage.getItem('__localeId') : 'en')
        },
        {
            provide: APP_INITIALIZER,
            useFactory: initializeAuthFactory,
            multi: true
        },
        provideHttpClient(withInterceptorsFromDi())
    ] })
export class AppModule {
	constructor(private injector: Injector) {
		// Create global Service Injector.
		if (!localStorage.getItem('__localeId')) {
			localStorage.setItem('__localeId', 'en');
		}
		void registerLocaleFor(localStorage.getItem('__localeId'));

		ServiceLocator.injector = this.injector;
		Cache.factory = new CacheFactory();
	}
}
